/**
 * Centred card carousel: the active card is larger and always centred, the
 * others sit either side and overflow the viewport. Swipe, the dots, arrow
 * keys or clicking a side card move between cards. Opens on the middle card.
 *
 * Markup: a track `[data-carousel-track]` holding `[data-carousel-card]`
 * children, plus optional `[data-dot-index]` buttons. Card sizes come from CSS
 * custom properties on the track:
 * - `--card-active-width`: the active card
 * - `--card-width`: its direct neighbours (`.is-near`)
 * - `--card-far-width`: every other card (optional, defaults to `--card-width`)
 *
 * Attributes:
 * - `loop`: no first or last card. Each card is placed by its circular
 *   distance from the active one, so the track wraps around. The track gets
 *   `data-loop-ready` once the cards are positioned; its CSS must then
 *   position the cards absolutely from the track's centre (left/top 50%).
 * - `autoplay-active`: only the active card's video plays; the rest are
 *   paused on their poster.
 *
 * Sound: a `[data-sound-toggle]` button inside a card turns sound on or off.
 * Only the active card's video is ever unmuted; the choice carries over as
 * the carousel moves, so the next active card plays with sound too.
 *
 * Used by sections/pdp-video-carousel.liquid and sections/shoppable-story-carousel.liquid.
 */
class CenteredCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.cards = Array.from(this.querySelectorAll('[data-carousel-card]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot-index]'));
    if (!this.track || this.cards.length === 0) return;

    this.loop = this.hasAttribute('loop') && this.cards.length > 1;
    this.index = Math.floor((this.cards.length - 1) / 2);

    this.soundOn = false;
    this.soundToggles = Array.from(this.querySelectorAll('[data-sound-toggle]'));
    this.soundToggles.forEach((button) => button.addEventListener('click', this.#onSoundToggle));

    this.cards.forEach((card, i) => card.addEventListener('click', () => this.goTo(i)));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
    this.track.addEventListener('keydown', this.#onKeydown);
    this.track.addEventListener('pointerdown', this.#onPointerDown);
    this.track.addEventListener('pointerup', this.#onPointerUp);
    // A swipe that ends on a link or button must not also activate it.
    this.track.addEventListener('click', this.#onClickCapture, true);

    this.resizeObserver = new ResizeObserver(() => this.#position(false));
    this.resizeObserver.observe(this);

    this.goTo(this.index, false);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  /**
   * @param {number} index
   * @param {boolean} [animate]
   */
  goTo(index, animate = true) {
    const previous = this.index;
    const count = this.cards.length;
    this.index = this.loop ? ((index % count) + count) % count : Math.max(0, Math.min(index, count - 1));

    this.cards.forEach((card, i) => {
      const distance = Math.abs(this.#offset(i));
      card.classList.toggle('is-active', distance === 0);
      card.classList.toggle('is-near', distance === 1);
      card.setAttribute('aria-current', distance === 0 ? 'true' : 'false');
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
    this.#position(animate);

    // Leave the videos alone when the active card is clicked again, so its
    // pause button keeps working.
    if (!animate || previous !== this.index) this.#syncVideos();
    this.#syncSound();
  }

  #onSoundToggle = (event) => {
    const card = event.currentTarget.closest('[data-carousel-card]');
    // A sound button on a side card is hidden; only act for the active card.
    if (this.cards.indexOf(card) !== this.index) return;
    this.soundOn = !this.soundOn;
    this.#syncSound(this.soundOn);
  };

  /**
   * Unmute only the active card's video (when sound is on); mute the rest.
   * @param {boolean} [resume] - also start the active video if it is paused.
   *   Only when the visitor turns sound on: goTo() runs this on every card
   *   click, including the pause button's, so resuming there undid the pause.
   */
  #syncSound(resume = false) {
    if (this.soundToggles.length === 0) return;
    this.cards.forEach((card, i) => {
      const audible = this.soundOn && i === this.index;
      card.querySelectorAll('video').forEach((video) => {
        video.muted = !audible;
        if (resume && audible && video.paused) video.play().catch(() => {});
      });
    });
    this.soundToggles.forEach((button) => {
      button.setAttribute('aria-pressed', String(this.soundOn));
      button.setAttribute('aria-label', this.soundOn ? button.dataset.muteLabel : button.dataset.unmuteLabel);
    });
  }

  /** Signed position of card `i` relative to the active card (wrapping when looped). */
  #offset(i) {
    let offset = i - this.index;
    if (!this.loop) return offset;

    const count = this.cards.length;
    offset = ((offset % count) + count) % count;
    return offset > Math.floor(count / 2) ? offset - count : offset;
  }

  // With `autoplay-active`, only the active card's video plays.
  #syncVideos() {
    if (!this.hasAttribute('autoplay-active')) return;
    this.cards.forEach((card, i) => {
      card.querySelectorAll('video').forEach((video) => {
        if (i === this.index) {
          video.muted = !this.soundOn;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    });
  }

  // Centre the active card. Offsets come from the CSS sizes rather than being
  // measured, because the card widths are still transitioning.
  #position(animate) {
    const styles = getComputedStyle(this.track);
    const nearWidth = parseFloat(styles.getPropertyValue('--card-width'));
    const farWidth = parseFloat(styles.getPropertyValue('--card-far-width')) || nearWidth;
    const activeWidth = parseFloat(styles.getPropertyValue('--card-active-width'));
    const gap = parseFloat(styles.columnGap) || 0;
    /** @param {number} distance */
    const widthAt = (distance) => (distance === 0 ? activeWidth : distance === 1 ? nearWidth : farWidth);

    this.track.classList.toggle('is-animating', animate);

    if (!this.loop) {
      let offset = activeWidth / 2;
      for (let i = 0; i < this.index; i++) offset += widthAt(this.index - i) + gap;
      this.track.style.transform = `translateX(${this.clientWidth / 2 - offset}px)`;
      return;
    }

    this.cards.forEach((card, i) => {
      const offset = this.#offset(i);
      const distance = Math.abs(offset);
      let center = 0;
      if (distance > 0) {
        center = activeWidth / 2 + gap + widthAt(distance) / 2;
        for (let d = 1; d < distance; d++) center += widthAt(d) + gap;
      }
      center *= Math.sign(offset);

      // A card wrapping round from one end to the other jumps instead of
      // sliding across the whole track.
      const wrapped = card.dataset.offset != null && Math.abs(offset - Number(card.dataset.offset)) > 1;
      card.dataset.offset = String(offset);
      card.classList.toggle('is-wrapping', wrapped);
      card.style.transform = `translate(calc(${center}px - 50%), -50%)`;
    });

    this.track.toggleAttribute('data-loop-ready', true);
  }

  #onKeydown = (event) => {
    if (event.key === 'ArrowRight') this.goTo(this.index + 1);
    if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
  };

  #onPointerDown = (event) => {
    this.pointerStartX = event.clientX;
    this.swiped = false;
  };

  #onPointerUp = (event) => {
    if (this.pointerStartX == null) return;
    const delta = event.clientX - this.pointerStartX;
    this.pointerStartX = null;
    if (Math.abs(delta) < 40) return;
    this.swiped = true;
    this.goTo(this.index + (delta < 0 ? 1 : -1));
  };

  #onClickCapture = (event) => {
    if (!this.swiped) return;
    this.swiped = false;
    event.preventDefault();
    event.stopPropagation();
  };
}

if (!customElements.get('centered-carousel')) {
  customElements.define('centered-carousel', CenteredCarousel);
}
